// Test script to verify all backend service connections including authentication
import { 
  jobService as backendJobService, 
  userService as backendUserService, 
  backendProviderService, 
  messagingService as backendCommunicationService,
  locationService,
  smartCategoryService,
  authService
} from '@/services';
import { API_CONFIG } from '@/config/api';

export interface ConnectionTestResult {
  service: string;
  endpoint: string;
  status: 'success' | 'error';
  message: string;
  responseTime?: number;
}

export class ConnectionTester {
  private results: ConnectionTestResult[] = [];

  async testAllConnections(): Promise<ConnectionTestResult[]> {
    this.results = [];
    
    console.log('🧪 Starting backend service connection tests...');

    try {
      // Debug: Check if services are imported correctly
      console.log('🔍 Checking service imports...');
      console.log('backendJobService:', typeof backendJobService);
      console.log('backendUserService:', typeof backendUserService);
      console.log('backendProviderService:', typeof backendProviderService);
      console.log('backendCommunicationService:', typeof backendCommunicationService);
      console.log('locationService:', typeof locationService);
      console.log('smartCategoryService:', typeof smartCategoryService);
      console.log('authService:', typeof authService);

      // Add a simple test to verify the testing framework works
      await this.testEndpoint(
        'Test Framework',
        'Basic Test',
        async () => {
          return '✅ Testing framework is working';
        }
      );

      // Run all tests in parallel for much faster execution
      console.log('🚀 Running all tests in parallel...');
      
      const testPromises = [
        // Authentication tests
        this.testAuthenticationFlow(),
        
        // Service tests - run in parallel
        this.testJobService(),
        this.testLocationService(),
        this.testSmartCategoryService(),
        this.testUserService(),
        this.testProviderService(),
        this.testCommunicationService(),
      ];

      // Wait for all tests to complete (much faster than sequential)
      await Promise.allSettled(testPromises);

      console.log('✅ All connection tests completed:', this.results);
      return this.results;
    } catch (error) {
      console.error('❌ Error during connection tests:', error);
      
      // Add error result
      this.results.push({
        service: 'Connection Tester',
        endpoint: 'Test Runner Error',
        status: 'error',
        message: `❌ Test runner failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
      
      return this.results;
    }
  }

  private async testAuthenticationFlow(): Promise<void> {
    // Test Cloudflare Functions authentication endpoints
    await this.testEndpoint(
      'Authentication Service',
      'Cloudflare Functions Health',
      async () => {
        // Test if Cloudflare Functions are accessible
        const baseUrl = window.location.origin;
        const response = await fetch(`${baseUrl}/auth/health`, { method: 'GET' });
        
        if (response.ok) {
          return '✅ Cloudflare Functions accessible';
        } else if (response.status === 404) {
          return '⚠️ Cloudflare Functions not deployed (auth endpoints may not work)';
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      }
    );

    // Test authentication state
    await this.testEndpoint(
      'Authentication Service',
      'Authentication State',
      async () => {
        const token = localStorage.getItem('auth_token');
        const userId = localStorage.getItem('user_id');
        
        if (token && userId) {
          // Test if token is valid by trying to get current user
          try {
            const user = await authService.getCurrentUser();
            if (user) {
              return `✅ Authenticated as ${user.email} (${user.id})`;
            } else {
              return '⚠️ Token exists but user data unavailable';
            }
          } catch (error) {
            return `⚠️ Token exists but invalid: ${error instanceof Error ? error.message : 'Unknown error'}`;
          }
        } else {
          return '❌ Not authenticated (no token found)';
        }
      }
    );

    // Test user sync to backend
    const token = localStorage.getItem('auth_token');
    const userId = localStorage.getItem('user_id');
    
    if (token && userId) {
      await this.testEndpoint(
        'Authentication Service',
        'User Sync to Backend',
        async () => {
          try {
            const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || API_CONFIG.BASE_URL}/api/v1/auth/users/sync`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'Origin': window.location.origin,
              },
              body: JSON.stringify({
                id: userId,
                email: 'test@example.com',
                firstName: 'Test',
                lastName: 'User',
                role: 'customer'
              })
            });

            if (response.ok) {
              return '✅ User sync to backend successful';
            } else if (response.status === 404) {
              return '⚠️ User service not available for sync';
            } else {
              const errorData = await response.json().catch(() => ({}));
              return `⚠️ User sync failed: HTTP ${response.status} - ${errorData.error || response.statusText}`;
            }
          } catch (error) {
            return `⚠️ User sync network error: ${error instanceof Error ? error.message : 'Unknown error'}`;
          }
        }
      );
    }

    // Test JWT token validation
    if (token) {
      await this.testEndpoint(
        'Authentication Service',
        'JWT Token Validation',
        async () => {
          try {
            // Test token with a protected endpoint
            const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || API_CONFIG.BASE_URL}/api/v1/auth/users/profile`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Origin': window.location.origin,
              }
            });

            if (response.ok) {
              return '✅ JWT token valid and accepted by backend';
            } else if (response.status === 401) {
              return '❌ JWT token rejected by backend (invalid or expired)';
            } else if (response.status === 404) {
              return '⚠️ User service not available for token validation';
            } else {
              return `⚠️ Token validation failed: HTTP ${response.status}`;
            }
          } catch (error) {
            return `⚠️ Token validation network error: ${error instanceof Error ? error.message : 'Unknown error'}`;
          }
        }
      );
    }
  }

  private async testJobService(): Promise<void> {
    // Run job service tests in parallel
    const jobTestPromises = [
      // Test job categories (public endpoint)
      this.testEndpoint(
        'Job Service',
        'GET /api/v1/jobs/categories',
        async () => {
          const categories = await backendJobService.getJobCategories();
          return `✅ Loaded ${categories.length} categories`;
        }
      ),

      // Test jobs list (public endpoint)
      this.testEndpoint(
        'Job Service',
        'GET /api/v1/jobs',
        async () => {
          const result = await backendJobService.getJobs({ limit: 5 });
          return `✅ Loaded ${result.jobs.length} jobs (total: ${result.total})`;
        }
      )
    ];

    // Wait for all job service tests to complete
    await Promise.allSettled(jobTestPromises);
  }

  private async testLocationService(): Promise<void> {
    // Run location service tests in parallel
    const locationTestPromises = [
      // Test counties (should be public)
      this.testEndpoint(
        'Location Service',
        'GET /api/v1/counties',
        async () => {
          const counties = await locationService.getCounties();
          return `✅ Loaded ${counties.length} counties`;
        }
      ),

      // Test areas (requires auth - skip if not authenticated)
      this.testEndpoint(
        'Location Service',
        'GET /api/v1/areas',
        async () => {
          const token = localStorage.getItem('auth_token');
          if (!token) {
            return '⚠️ Skipped - requires authentication (login first to test)';
          }
          const areas = await locationService.getAreas();
          return `✅ Loaded ${areas.length} areas`;
        }
      )
    ];

    // Wait for all location service tests to complete
    await Promise.allSettled(locationTestPromises);
  }

  private async testSmartCategoryService(): Promise<void> {
    // Test smart suggestions
    await this.testEndpoint(
      'Smart Category Service',
      'GET /api/v1/categories/suggest',
      async () => {
        const suggestions = await smartCategoryService.getSuggestions('plumbing repair');
        return `✅ Got ${suggestions.suggestions.length} suggestions`;
      }
    );
  }

  private async testUserService(): Promise<void> {
    const token = localStorage.getItem('auth_token');
    const userId = localStorage.getItem('user_id');

    if (!token || !userId) {
      this.results.push({
        service: 'User Service',
        endpoint: 'Authentication Required',
        status: 'error',
        message: '❌ No auth token or user ID found. Please log in first.'
      });
      return;
    }

    // Test user profile
    await this.testEndpoint(
      'User Service',
      'GET /api/v1/auth/users/profile',
      async () => {
        const profile = await backendUserService.getUserProfile();
        return `✅ Loaded profile for ${profile.firstName} ${profile.lastName}`;
      }
    );
  }

  private async testProviderService(): Promise<void> {
    // Test providers list
    await this.testEndpoint(
      'Provider Service',
      'GET /api/v1/providers',
      async () => {
        const providers = await backendProviderService.getProviders();
        return `✅ Loaded ${providers.length} providers`;
      }
    );
  }

  private async testCommunicationService(): Promise<void> {
    const token = localStorage.getItem('auth_token');
    const userId = localStorage.getItem('user_id');

    if (!token || !userId) {
      this.results.push({
        service: 'Communication Service',
        endpoint: 'Authentication Required',
        status: 'error',
        message: '❌ No auth token or user ID found. Please log in first.'
      });
      return;
    }

    // Test notifications
    await this.testEndpoint(
      'Communication Service',
      'GET /api/v1/communication/notifications',
      async () => {
        await backendCommunicationService.getNotifications();
        return `✅ Loaded notifications successfully`;
      }
    );
  }

  private async testEndpoint(
    service: string,
    endpoint: string,
    testFunction: () => Promise<string>
  ): Promise<void> {
    const startTime = Date.now();
    
    try {
      // Only log in development mode to reduce console spam
      if (import.meta.env.DEV) {
        console.log(`🔍 Testing ${service} - ${endpoint}...`);
      }
      
      const message = await testFunction();
      const responseTime = Date.now() - startTime;
      
      // Only log successful results in development
      if (import.meta.env.DEV) {
        console.log(`✅ ${service} - ${endpoint}: ${message} (${responseTime}ms)`);
      }
      
      this.results.push({
        service,
        endpoint,
        status: 'success',
        message,
        responseTime
      });
    } catch (error) {
      const responseTime = Date.now() - startTime;
      const errorMessage = `❌ ${error instanceof Error ? error.message : 'Unknown error'}`;
      
      // Always log errors but less verbosely
      console.error(`❌ ${service} - ${endpoint}: ${errorMessage} (${responseTime}ms)`);
      
      this.results.push({
        service,
        endpoint,
        status: 'error',
        message: errorMessage,
        responseTime
      });
    }
  }

  // Test individual service health endpoints
  async testHealthEndpoints(): Promise<ConnectionTestResult[]> {
    console.log('🏥 Testing health endpoints...');
    const healthResults: ConnectionTestResult[] = [];

    const healthEndpoints = [
      { service: 'Job Service (Jobs + Users)', url: `${import.meta.env.VITE_API_BASE_URL || API_CONFIG.BASE_URL}/api/v1/public/health/jobs` },
      { service: 'Provider Service', url: `${import.meta.env.VITE_API_BASE_URL || API_CONFIG.BASE_URL}/api/v1/public/health/providers` },
      { service: 'Communication Service', url: `${import.meta.env.VITE_API_BASE_URL || API_CONFIG.BASE_URL}/api/v1/public/health/communication` }
    ];

    // Run all health checks in parallel for much faster execution
    const healthPromises = healthEndpoints.map(async ({ service, url }) => {
      const startTime = Date.now();
      
      try {
        // Use shorter timeout for health checks (3 seconds)
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);
        
        const response = await fetch(url, {
          signal: controller.signal,
          method: 'GET',
          headers: {
            'Origin': window.location.origin,
            'Accept': 'application/json'
          }
        });
        
        clearTimeout(timeoutId);
        const responseTime = Date.now() - startTime;
        
        if (response.ok) {
          const data = await response.json();
          const message = `✅ ${data.status} (database: ${data.database})`;
          
          return {
            service,
            endpoint: 'Health Check',
            status: 'success' as const,
            message,
            responseTime
          };
        } else {
          const message = `❌ HTTP ${response.status}: ${response.statusText}`;
          
          return {
            service,
            endpoint: 'Health Check',
            status: 'error' as const,
            message,
            responseTime
          };
        }
      } catch (error) {
        const responseTime = Date.now() - startTime;
        const message = `❌ ${error instanceof Error ? error.message : 'Network error'}`;
        
        return {
          service,
          endpoint: 'Health Check',
          status: 'error' as const,
          message,
          responseTime
        };
      }
    });

    // Wait for all health checks to complete
    const results = await Promise.allSettled(healthPromises);
    
    // Extract successful results
    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        healthResults.push(result.value);
      } else {
        // Handle promise rejection
        healthResults.push({
          service: 'Unknown Service',
          endpoint: 'Health Check',
          status: 'error',
          message: `❌ Promise rejected: ${result.reason}`
        });
      }
    });

    console.log('🏥 Health endpoint tests completed:', healthResults);
    return healthResults;
  }

  // Generate a formatted report
  generateReport(results: ConnectionTestResult[]): string {
    const successCount = results.filter(r => r.status === 'success').length;
    const errorCount = results.filter(r => r.status === 'error').length;
    
    let report = `\n📊 Backend Service Connection Test Report\n`;
    report += `${'='.repeat(50)}\n`;
    report += `✅ Successful: ${successCount}\n`;
    report += `❌ Failed: ${errorCount}\n`;
    report += `📈 Success Rate: ${((successCount / results.length) * 100).toFixed(1)}%\n\n`;

    // Group by service
    const serviceGroups = results.reduce((groups, result) => {
      if (!groups[result.service]) {
        groups[result.service] = [];
      }
      groups[result.service].push(result);
      return groups;
    }, {} as Record<string, ConnectionTestResult[]>);

    Object.entries(serviceGroups).forEach(([service, serviceResults]) => {
      report += `🔧 ${service}\n`;
      report += `${'-'.repeat(30)}\n`;
      
      serviceResults.forEach(result => {
        const icon = result.status === 'success' ? '✅' : '❌';
        const time = result.responseTime ? ` (${result.responseTime}ms)` : '';
        report += `${icon} ${result.endpoint}${time}\n`;
        report += `   ${result.message}\n`;
      });
      
      report += '\n';
    });

    return report;
  }
}

// Export singleton instance
export const connectionTester = new ConnectionTester();

// Convenience function for quick testing
export async function testAllConnections(): Promise<void> {
  const results = await connectionTester.testAllConnections();
  const healthResults = await connectionTester.testHealthEndpoints();
  
  const allResults = [...results, ...healthResults];
  const report = connectionTester.generateReport(allResults);
  
  console.log(report);
  
  // Also display in browser if available
  if (typeof window !== 'undefined') {
    const successCount = allResults.filter(r => r.status === 'success').length;
    const totalCount = allResults.length;
    
    if (successCount === totalCount) {
      alert(`🎉 All ${totalCount} service connections are working perfectly!`);
    } else {
      alert(`⚠️ ${successCount}/${totalCount} connections working. Check console for details.`);
    }
  }
}